/**
 * ERC-20 reads and `approve`, through the connected wallet's provider (the SPA
 * has no RPC of its own). An unreadable value is `null`, never 0: a confident
 * "0.00" for a failed read sends the user into a failing submit.
 */

import { decodeFunctionResult, encodeFunctionData, parseAbi, type Hex } from "viem";
import { timing } from "../config";
import {
  ethCall,
  getTransactionReceipt,
  sendTransaction,
  WalletError,
  type TxReceipt,
} from "./wallet";

export const ERC20_ABI = parseAbi([
  "function balanceOf(address owner) view returns (uint256)",
  "function allowance(address owner, address spender) view returns (uint256)",
  "function approve(address spender, uint256 amount) returns (bool)",
]);

/** Unlimited allowance (2^256 - 1) — opt-in only, see `approve()`. */
export const MAX_UINT256 = (1n << 256n) - 1n;

/** Decodes a uint256 return value, or null when the call answered nothing. */
function decodeUint(
  functionName: "balanceOf" | "allowance",
  data: Hex | null | undefined,
): bigint | null {
  // "0x" is what an address with no code answers — unreadable, not zero.
  if (typeof data !== "string" || data.length <= 2) return null;
  try {
    const value = decodeFunctionResult({ abi: ERC20_ABI, functionName, data });
    return typeof value === "bigint" ? value : null;
  } catch {
    // Not a uint256 (an EOA, a non-standard token): unreadable, not zero.
    return null;
  }
}

/** Null when unreadable, and never throws: a display state, never a reason for a mock number. */
export async function readBalance(
  tokenAddress: string,
  owner: string,
): Promise<bigint | null> {
  try {
    const data = await ethCall(
      tokenAddress,
      encodeFunctionData({ abi: ERC20_ABI, functionName: "balanceOf", args: [owner as Hex] }),
      owner,
    );
    return decodeUint("balanceOf", data);
  } catch {
    return null;
  }
}

/**
 * Null when unreadable, which never means "needs approval": that would pin the
 * user behind an approve button that cannot succeed.
 */
export async function readAllowance(
  tokenAddress: string,
  owner: string,
  spender: string,
): Promise<bigint | null> {
  try {
    const data = await ethCall(
      tokenAddress,
      encodeFunctionData({
        abi: ERC20_ABI,
        functionName: "allowance",
        args: [owner as Hex, spender as Hex],
      }),
      owner,
    );
    return decodeUint("allowance", data);
  } catch {
    return null;
  }
}

/**
 * Resolves with the hash. Unlike the reads it throws, so the UI tells a rejection
 * (4001) from a fault.
 */
export function approve(
  owner: string,
  tokenAddress: string,
  spender: string,
  amount: bigint,
): Promise<Hex> {
  return sendTransaction({
    from: owner,
    to: tokenAddress,
    data: encodeFunctionData({
      abi: ERC20_ABI,
      functionName: "approve",
      args: [spender as Hex, amount],
    }),
  });
}

export interface WaitOptions {
  intervalMs?: number;
  /** Give up after this long and surface a timeout to the caller. */
  timeoutMs?: number;
}

/**
 * Polls until the transaction is mined. Signing must not follow the hash alone:
 * the allowance exists only once mined, and an earlier submit is refused.
 */
export async function waitForReceipt(
  hash: Hex,
  { intervalMs = timing.receiptPollMs, timeoutMs = timing.receiptTimeoutMs }: WaitOptions = {},
): Promise<TxReceipt> {
  const deadline = Date.now() + timeoutMs;
  for (;;) {
    const receipt = await getTransactionReceipt(hash).catch(() => null);
    if (receipt !== null) return receipt;
    if (Date.now() >= deadline) {
      throw new WalletError("RECEIPT_TIMEOUT", "Timed out waiting for the approval receipt");
    }
    await new Promise<void>((resolve) => {
      setTimeout(resolve, intervalMs);
    });
  }
}

/** True when a mined receipt reports a reverted transaction. */
export function isReverted(receipt: TxReceipt): boolean {
  const status = receipt.status;
  return status === "0x0" || status === "0x00" || status === 0 || status === false;
}
